import { useCallback } from "react"

import { useTranslation } from "react-i18next"

import type { UseCaseFilters } from "../../types/use-case.types"

import { Filter, Search, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { cn } from "@/lib/utils"

import { FEASIBILITY_MAP, STATUSES, TENK_COLORS } from "../../types/use-case.types"

type DashboardFiltersProps = {
  filters: UseCaseFilters
  allSites: string[]
  updateFilter: (key: keyof UseCaseFilters, value: unknown) => void
  clearFilters: () => void
}

function FilterDropdown({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string
  options: { value: string; label: string; color?: string }[]
  selected: Set<string>
  onToggle: (value: string) => void
}) {
  const hasFilter = selected.size > 0

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          className={cn("gap-1.5", hasFilter && "border-primary")}
          size="sm"
          variant="outline"
        >
          <Filter className="size-3.5" />
          {label}
          {hasFilter && (
            <span className="border-foreground bg-primary text-primary-foreground ml-1 rounded-full border px-1.5 text-xs">
              {selected.size}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-2">
        <div className="flex flex-col gap-1">
          {options.map((opt) => (
            <label
              key={opt.value}
              className="hover:bg-accent hover:text-accent-foreground flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5"
            >
              <Checkbox
                checked={selected.has(opt.value)}
                onCheckedChange={() => onToggle(opt.value)}
              />
              {opt.color && (
                <div className="size-2.5 rounded-full" style={{ backgroundColor: opt.color }} />
              )}
              <span className="text-sm">{opt.label}</span>
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

export function DashboardFilters({
  filters,
  allSites,
  updateFilter,
  clearFilters,
}: DashboardFiltersProps) {
  const { t } = useTranslation()

  const toggleSetValue = useCallback(
    (key: keyof UseCaseFilters, current: Set<string>, value: string) => {
      const next = new Set(current)
      if (next.has(value)) {
        next.delete(value)
      } else {
        next.add(value)
      }
      updateFilter(key, next)
    },
    [updateFilter]
  )

  const statusOptions = STATUSES.map((s) => ({
    value: s.text,
    label: s.text,
    color: s.color,
  }))

  const tenkOptions = (Object.keys(TENK_COLORS) as Array<keyof typeof TENK_COLORS>).map((key) => ({
    value: key,
    label: key,
    color: TENK_COLORS[key],
  }))

  const feasOptions = Object.entries(FEASIBILITY_MAP).map(([value, cfg]) => ({
    value,
    label: cfg.label,
    color: cfg.color,
  }))

  const siteOptions = allSites.map((s) => ({ value: s, label: s }))

  const hasAnyFilter =
    filters.statuses.size > 0 ||
    filters.tenkCategories.size > 0 ||
    filters.feasibilities.size > 0 ||
    filters.sites.size > 0 ||
    filters.searchText.length > 0

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative">
        <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2" />
        <Input
          className="h-8 w-56 pl-8 text-sm"
          placeholder={t("useCases.dashboard.filters.search")}
          value={filters.searchText}
          onChange={(e) => updateFilter("searchText", e.target.value)}
        />
      </div>

      <FilterDropdown
        label={t("useCases.dashboard.filters.status")}
        options={statusOptions}
        selected={filters.statuses}
        onToggle={(v) => toggleSetValue("statuses", filters.statuses, v)}
      />

      <FilterDropdown
        label={t("useCases.dashboard.filters.tenkRule")}
        options={tenkOptions}
        selected={filters.tenkCategories}
        onToggle={(v) => toggleSetValue("tenkCategories", filters.tenkCategories, v)}
      />

      <FilterDropdown
        label={t("useCases.dashboard.filters.feasibility")}
        options={feasOptions}
        selected={filters.feasibilities}
        onToggle={(v) => toggleSetValue("feasibilities", filters.feasibilities, v)}
      />

      <FilterDropdown
        label={t("useCases.dashboard.filters.site")}
        options={siteOptions}
        selected={filters.sites}
        onToggle={(v) => toggleSetValue("sites", filters.sites, v)}
      />

      {hasAnyFilter && (
        <Button className="gap-1" size="sm" variant="ghost" onClick={clearFilters}>
          <X className="size-3.5" />
          {t("useCases.dashboard.filters.clearAll")}
        </Button>
      )}
    </div>
  )
}
